// donut-chart.ts
// Receives chart-ready data from the page.
// Renders donut slices and a left-aligned legend.
// Emits filter requests when a slice is clicked.
export type DonutSlice = {
    key: string;      // machine key used for click/filter handling
    label: string;    // display label
    value: number;    // count
    color: string;    // slice + legend color
};

export type DonutChartData = {
    title?: string;
    total?: number;
    slices: DonutSlice[];
};

export type DonutFilterRequest = {
    preprint?: string[] | null;
};

export interface DonutChartOptions {
    rootSelector: string;
    onSliceClick?: (slice: DonutSlice) => void;
    activeSelectionText?: string | null;
    onClearSelection?: () => void;
}
export class DonutChart {
    private $root: JQuery;
    private data: DonutChartData = { slices: [] };

    constructor(private options: DonutChartOptions) {
        this.$root = $(options.rootSelector);
    }

    init(): void {
        this.wireClearButton();
        this.renderClearButton();
        this.render();
        this.bindClicks();

    }

    setData(data: DonutChartData): void {
        this.data = data;
        this.render();
    }
    private render(): void {
        const total = this.data.total ?? this.data.slices.reduce((sum, s) => sum + s.value, 0);

        const $title = this.$root.find('.donut-title');
        const $canvas = this.$root.find('.donut-canvas');
        const $legend = this.$root.find('.donut-legend');
        const $total = this.$root.find('.donut-total');

        $title.text(this.data.title ?? '');
        $total.text(`${total} total`);
        $canvas.empty();
        $legend.empty();

        if (!this.data.slices.length || total <= 0) {
            $canvas.html(`<div class="text-muted font-size12">No data</div>`);
            return;
        }

        const size = 150;
        const stroke = 28;
        const radius = (size - stroke) / 2;
        const cx = size / 2;
        const cy = size / 2;

        let currentAngle = 0;

        const paths = this.data.slices.map(slice => {
            const sweep = (slice.value / total) * 360;
            const startAngle = currentAngle;
            const endAngle = currentAngle + sweep;
            currentAngle = endAngle;

            // full circle edge case
            if (slice.value === total) {
                return `
                <circle
                    class="js-donut-slice"
                    data-key="${slice.key}"
                    cx="${cx}" cy="${cy}" r="${radius}"
                    fill="none"
                    stroke="${slice.color}"
                    stroke-width="${stroke}"
                    stroke-linecap="butt"
                ></circle>
            `;
            }

            return `
            <path
                class="js-donut-slice"
                data-key="${slice.key}"
                d="${this.describeArc(cx, cy, radius, startAngle, endAngle)}"
                fill="none"
                stroke="${slice.color}"
                stroke-width="${stroke}"
                stroke-linecap="butt"
            ></path>
        `;
        }).join('');

        $canvas.html(`
        <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
            <circle
                cx="${cx}" cy="${cy}" r="${radius}"
                fill="none"
                stroke="#e9ecef"
                stroke-width="${stroke}"
            ></circle>
            ${paths}
            <text x="50%" y="50%" text-anchor="middle" dominant-baseline="middle"
                  font-size="18" font-weight="600" fill="#333">
                ${total}
            </text>
        </svg>
    `);

        this.data.slices.forEach(slice => {
            $legend.append(`
            <div class="d-flex align-items-center justify-content-between mb-2 js-donut-legend-item"
                 data-key="${slice.key}" style="cursor:pointer;">
                <div class="d-flex align-items-center">
                    <span style="
                        display:inline-block;
                        width:10px;
                        height:10px;
                        border-radius:50%;
                        background:${slice.color};
                        margin-right:8px;
                    "></span>
                    <span>${slice.label}</span>
                </div>
                <span>${slice.value}</span>
            </div>
        `);
        });

    }
    private bindClicks(): void {
        this.$root
            .off('click.donut', '.js-donut-slice')
            .off('click.donut', '.js-donut-legend-item');

        this.$root.on('click.donut', '.js-donut-slice, .js-donut-legend-item', (e) => {
            const key = String($(e.currentTarget).data('key') ?? '').trim();
            const slice = this.data.slices.find(s => s.key === key);
            if (slice) {
                this.options.onSliceClick?.(slice);
            }
        });
    }
    private polarToCartesian(cx: number, cy: number, r: number, angleDeg: number) {
        const angleRad = (angleDeg - 90) * Math.PI / 180.0;
        return {
            x: cx + (r * Math.cos(angleRad)),
            y: cy + (r * Math.sin(angleRad))
        };
    }

    private describeArc(cx: number, cy: number, r: number, startAngle: number, endAngle: number) {
        const start = this.polarToCartesian(cx, cy, r, endAngle);
        const end = this.polarToCartesian(cx, cy, r, startAngle);
        const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";

        return [
            "M", start.x, start.y,
            "A", r, r, 0, largeArcFlag, 0, end.x, end.y
        ].join(" ");
    }

    setActiveSelection(text: string | null): void {
        this.options.activeSelectionText = text;
        this.renderClearButton();
    }

    private renderClearButton(): void {
        const $header = this.$root.find('.js-card-header');
        const $card = this.$root.closest('.col-20');

        if (!$header.length || !$card.length) return;

        $header.find('.js-clear-selection').remove();
      
        const text = (this.options.activeSelectionText ?? '').trim();

        if (!text) {
            $card.removeClass('border-select');
            return;
        }

        $card.addClass('border-select');

        $header.append(`
        <button type="button"
                class="clear-btn btn-sm js-clear-selection">
            Clear
        </button>
    `);
        
    }

    private wireClearButton(): void {
        this.$root
            .off('click.clearSelection')
            .on('click.clearSelection', '.js-clear-selection', (e) => {
                e.preventDefault();
                e.stopImmediatePropagation();

                this.options.onClearSelection?.();
            });
    }
}